/**
 * 组件实例 transform root 索引。
 *
 * 文件职责：
 * 1. 在 cad-core 内部维护 Object3D / assemblyNodeId 到可写 transform root 的缓存；
 * 2. 避免 Mate 拖动实时求解路径反复 scene.traverse；
 * 3. 只保存运行时弱语义索引，不参与文档持久化，也不进入 Vue 响应式系统。
 */

import type { Object3D } from 'three/webgpu';
import type { ComponentInstanceTransformRootIndexHost, ComponentInstanceTransformTargetResolverHost } from '../../../types';
export type { ComponentInstanceTransformRootIndexHost } from '../../../types';
import { readComponentInstanceAssemblyNodeId } from './resolveComponentInstanceTransformRoot';
import {
  resolveComponentInstanceTransformTarget,
  isImportedTopologyInteractionObject,
} from './ComponentInstanceTransformTargetResolver';
import {
  shouldSkipComponentInstanceTraversalSubtree,
  isInsideSkippedComponentInstanceTraversalSubtree,
} from './utils/ComponentInstanceMetadata';
import {
  areAssemblyNodeIdentityEquivalent,
  collectAssemblyNodeIdentityLookupIds,
} from '../utils/AssemblyNodePath';

/**
 * 组件实例 transform root 索引。
 *
 * 生命周期：
 * - 首次查询时按当前 scene 懒构建；
 * - scene 变化或外部显式 invalidate 后重建；
 * - dispose 时解除 Object3D 引用，避免跨模型缓存污染。
 */
export class ComponentInstanceTransformRootIndex {
  private readonly host: ComponentInstanceTransformRootIndexHost;
  private indexedScene: Object3D | null = null;
  private readonly objectById = new Map<string, Object3D>();
  private readonly rootByObjectId = new Map<string, Object3D>();
  private readonly rootByAssemblyNodeId = new Map<string, Object3D>();
  private readonly ambiguousAssemblyNodeLookupIds = new Set<string>();
  /** 一次逻辑树遍历构建的原始身份候选桶，所有对象解析共享，失效时整体释放。 */
  private readonly objectsByAssemblyNodeId = new Map<string, Object3D[]>();
  /** 记录进入场景时的业务成员；移除时父链可能已断开，不能重新猜测旧归属。 */
  private readonly businessMembers = new Set<Object3D>();
  /** 同一套解析规则同时服务批量重建和短命拓扑 UUID 查询。 */
  private readonly resolverHost: ComponentInstanceTransformTargetResolverHost;
  /** 当前缓存对应的身份/实例投影版本，不随纯位姿变化重建。 */
  private indexedSemanticRevision = -1;
  /** 与索引生命周期配对的结构订阅。 */
  private unsubscribeMembership: (() => void) | null = null;
  /** 销毁后禁止重新收集外部场景引用。 */
  private disposed = false;

  /**
   * 构造函数。
   *
   * @param host transform root 索引宿主。
   */
  public constructor(host: ComponentInstanceTransformRootIndexHost) {
    this.host = host;
    this.resolverHost = {
      getScene: () => host.getScene(),
      getSceneObjectById: (objectId) => this.objectById.get(objectId) ?? null,
      forEachAssemblyNodeCandidate: (assemblyNodeId, visitor) => {
        this.objectsByAssemblyNodeId.get(assemblyNodeId)?.forEach(visitor);
      },
    };
    this.unsubscribeMembership = host.membershipSource?.subscribe({
      onObjectAdded: (object) => {
        if (this.isAuxiliaryObject(object)) {
          // 短命拓扑仍能按 UUID 查询，但不得改变组件身份索引；调试子树保持不可选。
          if (!isInsideSkippedComponentInstanceTraversalSubtree(object)) {
            this.objectById.set(object.uuid, object);
          }
          return;
        }
        this.businessMembers.add(object);
        this.invalidate();
      },
      onObjectRemoved: (object) => {
        // 元数据修订可能已把原辅助成员提升为业务对象，重建后的映射同样是失效依据。
        const wasIndexedBusinessObject = this.rootByObjectId.has(object.uuid);
        this.objectById.delete(object.uuid);
        this.rootByObjectId.delete(object.uuid);
        const wasObservedBusinessObject = this.businessMembers.delete(object);
        if (wasObservedBusinessObject || wasIndexedBusinessObject) this.invalidate();
      },
    }) ?? null;
  }

  /**
   * 使当前索引失效。
   */
  public invalidate(): void {
    this.indexedScene = null;
    this.indexedSemanticRevision = -1;
    this.objectById.clear();
    this.rootByObjectId.clear();
    this.rootByAssemblyNodeId.clear();
    this.ambiguousAssemblyNodeLookupIds.clear();
    this.objectsByAssemblyNodeId.clear();
  }

  /**
   * 释放索引缓存。
   */
  public dispose(): void {
    this.disposed = true;
    this.unsubscribeMembership?.();
    this.unsubscribeMembership = null;
    this.businessMembers.clear();
    this.invalidate();
  }

  /**
   * 按 Object3D uuid / assemblyNodeId 查询 transform root。
   *
   * @param objectId 运行时对象 ID 或稳定装配节点 ID。
   * @returns 可写 transform root；缺失时返回 null。
   */
  public resolveByObjectId(objectId: string | null | undefined): Object3D | null {
    if (this.disposed || !objectId) {
      return null;
    }
    this.ensureIndex();
    const cachedRoot = this.resolveKnownObjectRoot(objectId)
      ?? this.resolveRootByAssemblyNodeIdLookup(objectId)
      ?? null;
    if (cachedRoot) {
      return cachedRoot;
    }

    // 已订阅成员变化时，未命中就是当前快照的结果，不能让每条历史查询重新扫描场景。
    if (this.host.membershipSource) return null;
    // 兼容没有结构通知的独立 SDK 宿主，仍允许未命中时发现直接插入的普通对象。
    this.invalidate();
    this.ensureIndex();
    return this.resolveKnownObjectRoot(objectId)
      ?? this.resolveRootByAssemblyNodeIdLookup(objectId)
      ?? null;
  }

  /**
   * 按 Object3D uuid / assemblyNodeId 查询原始场景对象或组件根。
   *
   * @param objectId 运行时对象 ID 或稳定装配节点 ID。
   * @returns 原始场景对象或组件 transform root；缺失时返回 null。
   */
  public resolveObjectById(objectId: string | null | undefined): Object3D | null {
    if (this.disposed || !objectId) {
      return null;
    }
    this.ensureIndex();
    const cachedObject = this.objectById.get(objectId)
      ?? this.resolveRootByAssemblyNodeIdLookup(objectId)
      ?? null;
    if (cachedObject) {
      return cachedObject;
    }

    if (this.host.membershipSource) return null;
    this.invalidate();
    this.ensureIndex();
    return this.objectById.get(objectId)
      ?? this.resolveRootByAssemblyNodeIdLookup(objectId)
      ?? null;
  }

  /**
   * 按稳定装配节点 ID 查询 transform root。
   *
   * @param assemblyNodeId 装配节点 ID。
   * @returns 可写 transform root；缺失时返回 null。
   */
  public resolveByAssemblyNodeId(assemblyNodeId: string | null | undefined): Object3D | null {
    if (this.disposed || !assemblyNodeId) {
      return null;
    }
    this.ensureIndex();
    const cachedRoot = this.resolveRootByAssemblyNodeIdLookup(assemblyNodeId);
    if (cachedRoot) {
      return cachedRoot;
    }

    if (this.host.membershipSource) return null;
    this.invalidate();
    this.ensureIndex();
    return this.resolveRootByAssemblyNodeIdLookup(assemblyNodeId);
  }

  /**
   * 确保当前 scene 已构建索引。
   */
  private ensureIndex(): void {
    const scene = this.host.getScene();
    if (!scene) {
      this.invalidate();
      return;
    }
    const semanticRevision = this.host.getSemanticRevision?.() ?? 0;
    if (this.indexedScene === scene && this.indexedSemanticRevision === semanticRevision) {
      return;
    }

    this.rebuild(scene);
    this.indexedSemanticRevision = semanticRevision;
  }

  /**
   * 重建当前 scene 的 transform root 索引。
   *
   * @param scene 当前场景。
   */
  private rebuild(scene: Object3D): void {
    this.invalidate();
    this.indexedScene = scene;
    const objects: Object3D[] = [];

    this.traverseIndexableObjects(scene, (object) => {
      this.registerObjectId(object.uuid, object);
      if (this.isAuxiliaryObject(object)) return;
      objects.push(object);
      const assemblyNodeId = readComponentInstanceAssemblyNodeId(object);
      if (assemblyNodeId) {
        const candidates = this.objectsByAssemblyNodeId.get(assemblyNodeId);
        if (candidates) candidates.push(object);
        else this.objectsByAssemblyNodeId.set(assemblyNodeId, [object]);
      }
    });

    objects.forEach((object) => {
      const transformRoot = resolveComponentInstanceTransformTarget(object, this.resolverHost);
      if (!transformRoot || transformRoot === scene) {
        return;
      }

      this.registerRootForObject(object, transformRoot);
      this.registerRootForObject(transformRoot, transformRoot);
      const assemblyNodeId = readComponentInstanceAssemblyNodeId(object)
        || readComponentInstanceAssemblyNodeId(transformRoot);
      this.registerRootForAssemblyNodeId(assemblyNodeId, transformRoot);
    });
  }

  /**
   * 按运行时 UUID 解析已知对象。拓扑代理只按需解析，不向稳定装配身份表注册自身。
   * @param objectId 运行时对象 ID。
   * @returns 稳定组件根，或者当前拓扑代理父链对应的组件根。
   */
  private resolveKnownObjectRoot(objectId: string): Object3D | null {
    const cached = this.rootByObjectId.get(objectId);
    if (cached) return cached;
    const object = this.objectById.get(objectId);
    return object && this.isAuxiliaryObject(object)
      ? resolveComponentInstanceTransformTarget(object, this.resolverHost)
      : null;
  }

  /**
   * 判断对象是否属于辅助显示或拓扑交互子树，不能把其物理增删解释为组件结构变化。
   * @param object 当前成员；进入场景时父链完整。
   * @returns 辅助对象及其后代返回 true，普通结果体和业务组件返回 false。
   */
  private isAuxiliaryObject(object: Object3D): boolean {
    let current: Object3D | null = object;
    while (current) {
      if (shouldSkipComponentInstanceTraversalSubtree(current) || isImportedTopologyInteractionObject(current)) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  /**
   * 注册 Object3D 查询 ID。
   *
   * @param objectId Object3D uuid。
   * @param object 场景对象。
   */
  private registerObjectId(objectId: string | null | undefined, object: Object3D): void {
    if (!objectId) {
      return;
    }
    this.objectById.set(objectId, object);
  }

  /**
   * 注册对象到 transform root 的映射。
   *
   * @param object 源对象。
   * @param transformRoot 可写 transform root。
   */
  private registerRootForObject(object: Object3D, transformRoot: Object3D): void {
    this.rootByObjectId.set(object.uuid, transformRoot);

    const assemblyNodeId = readComponentInstanceAssemblyNodeId(object);
    this.registerRootForAssemblyNodeId(assemblyNodeId, transformRoot);
  }

  /**
   * 按装配节点等价身份查询 transform root。
   *
   * @param assemblyNodeId 装配节点 ID、编码 ID 或 lookup ID。
   * @returns 命中的 transform root；未命中时返回 null。
   */
  private resolveRootByAssemblyNodeIdLookup(assemblyNodeId: string | null | undefined): Object3D | null {
    for (const lookupId of collectAssemblyNodeIdentityLookupIds(assemblyNodeId)) {
      if (this.ambiguousAssemblyNodeLookupIds.has(lookupId)) {
        continue;
      }
      const root = this.rootByAssemblyNodeId.get(lookupId);
      if (root) {
        return root;
      }
    }
    return this.resolveUniqueRootByEquivalentAssemblyNodeId(assemblyNodeId);
  }

  /**
   * 精确 lookup 未命中时，按装配路径尾部等价关系兜底查找唯一 transform root。
   *
   * 设计说明：
   * `.puppy` 子装配被父工作区挂载后，Mate 文档中的 ID 可能是“父路径/局部节点”，
   * 而真实运行时对象只注册了“局部节点”。只有唯一根命中时才返回，避免同名零件出现在多个子装配时误选。
   *
   * @param assemblyNodeId 装配节点 ID、编码 ID 或 lookup ID。
   * @returns 唯一命中的 transform root；无命中或多根歧义时返回 null。
   */
  private resolveUniqueRootByEquivalentAssemblyNodeId(
    assemblyNodeId: string | null | undefined,
  ): Object3D | null {
    const matchedRoots = new Map<string, Object3D>();
    for (const [registeredAssemblyNodeId, root] of this.rootByAssemblyNodeId.entries()) {
      if (!areAssemblyNodeIdentityEquivalent(registeredAssemblyNodeId, assemblyNodeId)) {
        continue;
      }
      matchedRoots.set(root.uuid, root);
      if (matchedRoots.size > 1) {
        return null;
      }
    }
    return [...matchedRoots.values()][0] ?? null;
  }

  /**
   * 注册装配节点 ID 的全部等价 lookup 入口。
   *
   * 设计说明：
   * 子装配离线包的 Mate 可能保留 URL 编码 ID，而父工作区 Object3D metadata 通常使用解码后的语义 ID。
   * 索引层统一展开等价身份，避免高亮、求解和预览入口各自补兼容分支。
   *
   * @param assemblyNodeId 装配节点 ID。
   * @param transformRoot 可写 transform root。
   */
  private registerRootForAssemblyNodeId(
    assemblyNodeId: string | null | undefined,
    transformRoot: Object3D,
  ): void {
    for (const lookupId of collectAssemblyNodeIdentityLookupIds(assemblyNodeId)) {
      if (this.ambiguousAssemblyNodeLookupIds.has(lookupId)) {
        continue;
      }

      const existingRoot = this.rootByAssemblyNodeId.get(lookupId);
      if (!existingRoot) {
        this.rootByAssemblyNodeId.set(lookupId, transformRoot);
        continue;
      }

      if (existingRoot !== transformRoot) {
        /**
         * 同源装配多实例会共享去实例编号后的 lookup key。
         * 该 key 一旦命中多棵 transform root，就只能作为唯一等价回退的候选，不能继续固定到第一棵树。
         */
        this.rootByAssemblyNodeId.delete(lookupId);
        this.ambiguousAssemblyNodeLookupIds.add(lookupId);
      }
    }
  }

  /**
   * 遍历允许参与组件 transform 解析的对象。
   *
   * @param root 当前场景根。
   * @param visitor 访问函数。
   */
  private traverseIndexableObjects(root: Object3D, visitor: (object: Object3D) => void): void {
    const visitedObjects = new Set<Object3D>();
    const visit = (object: Object3D): void => {
      if (visitedObjects.has(object)) {
        return;
      }
      visitedObjects.add(object);
      if (object !== root && shouldSkipComponentInstanceTraversalSubtree(object)) {
        return;
      }

      visitor(object);
      const logicalChildren = this.host.listLogicalChildren?.(object) ?? object.children;
      logicalChildren.forEach((child) => {
        visit(child as Object3D);
      });
    };

    visit(root);
  }
}
